// src/features/files/components/FileExplorer/modules/FolderGlyph.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip } from '@mui/material';
import {
  Folder as FolderIcon,
  Link as LinkIcon,
  Lock as LockIcon,
  LockPerson as LockPersonIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { isOwnItem } from '../../../utils/getFolderName';

const BADGES = { shared: PublicIcon, system: LockIcon, linked: LinkIcon };

/**
 * Folder icon. Folders Orbit manages (see getFolderLink) get a small badge:
 * a globe on folders shared with all departments, a lock on its own folders,
 * a link on folders that belong to a piece of equipment, a room, a task or an item.
 */
export function FolderGlyph({ link, size = 20, color = 'primary.main', className }) {
  const Badge = BADGES[link?.type] || LinkIcon;
  // Relatively bigger on small icons, or it can't be made out
  const badge = size <= 24 ? 0.66 : 0.55;
  return (
    <Box component="span" className={className} sx={{ position: 'relative', display: 'inline-flex' }}>
      <FolderIcon sx={{ fontSize: size, color }} />
      {link && (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            right: -size * 0.2,
            bottom: -size * 0.1,
            width: size * badge,
            height: size * badge,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            boxShadow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Badge sx={{ fontSize: size * badge * 0.72, color: 'text.secondary' }} />
        </Box>
      )}
    </Box>
  );
}

/** Type label and explanation for a folder Orbit manages; null for the department's own folders. */
export function useFolderLinkText(link) {
  const { t } = useTranslation(['files']);
  if (!link) return null;
  if (link.type === 'shared') {
    return {
      label: t('files:shared.label'),
      hint: link.own
        ? t('files:shared.own_hint')
        : t('files:shared.other_hint', { department: link.department || t('files:shared.managing_department') }),
    };
  }
  if (link.type === 'system') {
    return { label: t('files:linked.system_label'), hint: t('files:linked.system_hint') };
  }
  return {
    label: t('files:linked.linked_label'),
    hint: t('files:linked.linked_hint', {
      kind: t(`files:linked.kinds.${link.kind}`, { defaultValue: t('files:linked.kinds.other') }),
    }),
  };
}

/**
 * Globe on a file another department shares with all departments: it can be
 * opened and downloaded here, but only that department changes it. `badge`
 * puts it on a white disc, for drawing over a thumbnail.
 */
export function SharedFileMark({ file, badge = false, sx }) {
  const { t } = useTranslation(['files']);
  if (!file?.is_system_file || isOwnItem(file)) return null;
  const department = file.department_name || t('files:shared.managing_department');
  return (
    <Tooltip title={t('files:shared.file_hint', { department })}>
      <Box
        component="span"
        aria-label={t('files:shared.label')}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          ...(badge && { width: 22, height: 22, borderRadius: '50%', bgcolor: 'background.paper', boxShadow: 1 }),
          ...sx,
        }}
      >
        <PublicIcon sx={{ fontSize: badge ? 15 : 16 }} />
      </Box>
    </Tooltip>
  );
}

/**
 * Marks a file or folder whose access is limited: by rules of its own (orange)
 * or by a folder above it (grey).
 */
export function AccessMark({ item, badge = false, sx }) {
  const { t } = useTranslation(['files']);
  const own = Boolean(item?.has_access_rules);
  if (!own && !item?.access_restricted) return null;
  return (
    <Tooltip title={t(own ? 'files:access.restricted_hint' : 'files:access.restricted_by_folder_hint')}>
      <Box
        component="span"
        aria-label={t('files:access.restricted')}
        data-testid="access-mark"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: own ? 'warning.main' : 'text.disabled',
          ...(badge && { width: 22, height: 22, borderRadius: '50%', bgcolor: 'background.paper', boxShadow: 1 }),
          ...sx,
        }}
      >
        <LockPersonIcon sx={{ fontSize: badge ? 15 : 16 }} />
      </Box>
    </Tooltip>
  );
}
